import { branch } from '@ipfabric/utils/fp/function';
import { prop } from '@ipfabric/utils/fp/prop';
import { option } from 'fp-ts';
import type { Option } from 'fp-ts/lib/Option';
import { flow, identity, pipe } from 'fp-ts/lib/function';
import type {
  AnimationFn,
  RunningAnimation,
} from 'utils/pixi/animation/AnimationController';
import { fromUnitInterval } from 'utils/pixi/animation/fromUnitInterval';

export interface BouncingAnimationController {
  backward: () => Option<RunningAnimation>;
  forward: () => Option<RunningAnimation>;
  getRunningAnimation: () => Option<RunningAnimation>;
  isIdle: () => boolean;
}

type TransformPhase = (phase: number) => number;

interface ActiveAnimation {
  animation: RunningAnimation;
  length: number;
  transformPhase: TransformPhase;
}

export type AnimationDirection = boolean;
export const ANIMATION_FORWARD = true;
export const ANIMATION_BACKWARD = false;

export function createBouncingAnimationController(
  animate: AnimationFn,
  lengthMilliseconds: number,
  apply: (phase: number, direction: AnimationDirection) => void,
  ease: TransformPhase = identity,
): BouncingAnimationController {
  if (
    process.env.NODE_ENV === 'development' ||
    process.env.NODE_ENV === 'test'
  ) {
    if (ease(0) !== 0 || ease(1) !== 1) {
      throw new Error(
        `Easing function must be only controlling speed between 0 and 1, therefore map 0->0 and 1->1, \
otherwise reversing the animation will not work properly. You should transform the timing value to the animation \
value in the apply() callback. Your function evaluates as ease(0)=${ease(0)}, ease(1)=${ease(1)}.`,
      );
    }
  }

  /**
   * Right: animation runs forward
   * Left: animation runs backward
   * None: animation is idle
   */
  let activeAnimation: Option<ActiveAnimation> = option.none;
  let activeDirection: AnimationDirection = ANIMATION_BACKWARD;
  const inverseEase = flow(ease, fromUnitInterval(1, 0));
  const curriedApply = (direction: AnimationDirection) => (phase: number) =>
    apply(phase, direction);

  const startAs = (direction: AnimationDirection) =>
    branch(async (a: ActiveAnimation) => {
      activeAnimation = option.some(a);
      activeDirection = direction;

      await a.animation.promise;
      if (option.toNullable(activeAnimation) === a) {
        // remove the animation only in case no other animation has been started
        activeAnimation = option.none;
      }
    });

  const createActiveAnimation = (
    length: number,
    transformPhase: TransformPhase,
    direction: AnimationDirection,
  ): ActiveAnimation => ({
    animation: animate(length, flow(transformPhase, curriedApply(direction))),
    transformPhase,
    length,
  });

  const startFromIdle =
    (direction: AnimationDirection, transformPhase: TransformPhase) => () =>
      createActiveAnimation(lengthMilliseconds, transformPhase, direction);
  const getRemainingTime = ({ length, animation }: ActiveAnimation): number =>
    lengthMilliseconds - length + animation.getElapsedTime();

  const bounce =
    (direction: AnimationDirection, targetValue: 0 | 1) =>
    (otherDirectionAnimation: ActiveAnimation) => {
      otherDirectionAnimation.animation.cancel();
      return createActiveAnimation(
        // when bouncing an animation, the reversed animation lasts
        // (original total length - remaining length of last animation)
        getRemainingTime(otherDirectionAnimation),
        flow(
          ease,
          // start the animation at the value where the other direction left off
          fromUnitInterval(
            getTransformedPhase(otherDirectionAnimation),
            targetValue,
          ),
        ),
        direction,
      );
    };

  const run = (
    direction: AnimationDirection,
    easeFn: TransformPhase,
    targetValue: 0 | 1,
  ) =>
    direction === activeDirection
      ? pipe(
          // when already animating/last animated in this direction, just pass current active animation if any
          activeAnimation,
          option.map(extractRunningAnimation),
        )
      : pipe(
          // start animation
          activeAnimation,
          option.fold(
            startFromIdle(direction, easeFn),
            bounce(direction, targetValue),
          ),
          startAs(direction),
          extractRunningAnimation,
          option.some,
        );
  return {
    backward: () => run(ANIMATION_BACKWARD, inverseEase, 0),
    forward: () => run(ANIMATION_FORWARD, ease, 1),
    getRunningAnimation: () =>
      pipe(activeAnimation, option.map(prop('animation'))),
    isIdle: () => option.isNone(activeAnimation),
  };
}

function extractRunningAnimation({
  animation,
}: ActiveAnimation): RunningAnimation {
  return animation;
}

function getTransformedPhase(animation: ActiveAnimation): number {
  return animation.transformPhase(animation.animation.getPhase());
}
